import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
      {
        title: 'Section 508 ICT Testing Baseline',
        subtitle: '[ictbaseline.access-board.gov]',
        linkUrl: REFERENCE_LINKS.ictTestingBaseline,
      },
      {
        title: 'Learn about writing in plain language ',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
    ],
  },
];

const ContainmentStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Containment"
        breadcrumbs={[
          { label: 'General Components', url: '/general-components' },
          { label: 'Containment' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="modal-dialog">
                  Modal dialog
                </h3>
                <p className="mb-40">
                  Modal dialogs are used to capture the user&apos;s attention for a focused
                  interaction, typically requiring confirmation or action before proceeding. They
                  should be used sparingly and for important tasks or decisions to avoid
                  interrupting the user&apos;s flow unnecessarily. The content within a modal should
                  be concise, clear, and actionable, ensuring users can easily understand what’s
                  required. Additionally, modals should have a clear way to close, such as a cancel
                  or close button, allowing users to exit without completing the action.
                </p>
                <h3 className="h3" id="12-column-grid">
                  12 Column Grid
                </h3>
                <p className="mb-40">
                  The 12-column grid is a flexible layout system used to organize content on a page.
                  It divides the page into 12 equal columns, offering versatility in creating
                  complex and balanced designs. Content can span across one or more columns,
                  allowing for a responsive layout that adapts to different screen sizes. The grid
                  helps ensure consistent alignment, spacing, and a cohesive visual flow. It also
                  supports various content arrangements, such as centered elements, sidebars, and
                  full-width sections, while maintaining a clean and structured appearance.
                </p>
              </div>
              <img
                src="/images/general-components-containment/containment-1@2x.webp"
                width={1000}
                height={670}
                alt="12 Column Grid"
                className="mb-40 img-responsive"
              />
              <div className="page-content-item">
                <h2 className="divider h1" id="specifications">
                  Specifications
                </h2>
                <h3 className="h3" id="the-modals-anatomy">
                  The Modals Anatomy
                </h3>
                <p className="mb-8">The anatomy of a modal includes several key components:</p>
                <ol className="numbered-list">
                  <li>
                    <strong>Backdrop:</strong> The area behind the modal that typically dims to
                    focus attention on the modal content.
                  </li>
                  <li>
                    <strong>Header:</strong> Contains the modal title and optional close button,
                    helping users understand the purpose of the modal.
                  </li>
                  <li>
                    <strong>Body:</strong> The main content area where the information or
                    interactive elements of the modal reside.
                  </li>
                  <li>
                    <strong>Footer:</strong> Includes actions like buttons (e.g., Cancel, Confirm)
                    to either close the modal or submit actions.
                  </li>
                </ol>
                <p className="mb-40">
                  These components work together to provide a clear and focused interaction, while
                  allowing users to understand the modal’s context and take necessary actions.
                </p>
              </div>
              <img
                src="/images/general-components-containment/containment-2@2x.webp"
                width={1000}
                height={488}
                alt="Modals Anatomy"
                className="mb-40 img-responsive"
              />
              <div className="page-content-item">
                <h3 className="h3" id="grids-anatomy">
                  Grid’s Anatomy
                </h3>
                <p className="mb-40">
                  The anatomy of a grid includes several components that work together to create a
                  structured layout. The grid container is the outer wrapper that holds and aligns
                  all grid items, defining the overall layout of the grid. Inside the container,
                  grid rows serve as horizontal sections that group content or elements in a line
                  across the container. Grid columns are the vertical divisions that align content
                  in a structured and consistent manner. Finally, grid items are the individual
                  content blocks placed within the grid, occupying specific cells according to the
                  row and column definitions. These elements allow for responsive layouts, ensuring
                  that content adapts to different screen sizes while maintaining consistency and
                  organization.
                </p>
              </div>
              <img
                src="/images/general-components-containment/containment-3@2x.webp"
                width={1000}
                height={590}
                alt="Grid’s Anatomy"
                className="mb-40 img-responsive"
              />
              <div className="page-content-item">
                <h2 className="h1 divider" id="guidelines">
                  Guidelines
                </h2>
                <h3 className="h3" id="modals-usage">
                  Modals usage
                </h3>
                <p className="mb-8">
                  Use modal dialogs to present a short-term task the user needs to perform. This can
                  include critical or warning information where a response is required, however
                  they&apos;re also intended to support efficient task completion without losing the
                  context of the underlying page. Users won&apos;t be able to interact with the page
                  until the dialog is closed.
                </p>
                <ol className="disc-list mb-40">
                  <li>Modal dialog titles should use sentence case and prioritize keywords.</li>
                  <li>
                    Modal dialog body copy should contain only valuable and relevant information
                    that is both helpful and concise.
                  </li>
                  <li>
                    Label elements with action verbs that indicate what happens when the element is
                    selected. For example, label a select menu with Choose a user instead of Users.
                  </li>
                  <li>
                    The main action (a primary button) should reflect the modal title. For example,
                    a modal with the title &quot;Fork «repository name&gt;&quot; has a button
                    labeled &quot;Fork repository&quot;. The title &quot;Select a template&quot; has
                    a button labeled &quot;Select&quot;.
                  </li>
                </ol>
                <h3 className="h3" id="grids-usage">
                  Grid’s usage
                </h3>
                <p className="mb-40">
                  Grids help organize content, ensuring a consistent layout across devices. They
                  provide alignment, spacing, and hierarchy, making the design visually balanced and
                  easy to navigate. Use grids to create a clear flow, avoid overcrowding, and
                  enhance user experience and accessibility.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-containment/screen-do-use-grids-usage@2x.webp"
                    width={488}
                    height={422}
                    alt="Do use grids usage"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p className="text-paragraph-large">
                      Using an odd grid configuration in UI web design can create visually
                      interesting and dynamic layouts, offering flexibility and emphasizing
                      hierarchy.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    src="/images/general-components-containment/screen-do-not-grids-usage@2x.webp"
                    width={488}
                    height={422}
                    alt="Do not use grids usage"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p className="text-paragraph-large">
                      Avoid going completely off-grid as it can lead to inconsistent layouts, making
                      it difficult for users to navigate and understand the content.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="grids-content-hierarchy">
                  Grid’s content hierarchy
                </h3>
                <p className="mb-40">
                  Grid content hierarchy defines the structure and prioritization of elements within
                  a layout. It ensures that the most important information stands out, guiding the
                  user’s eye through the page in a logical order. By using consistent alignment and
                  spacing, grids enhance clarity and focus, improving both usability and aesthetic
                  appeal.
                </p>
              </div>
              <img
                src="/images/general-components-containment/containment-4@2x.webp"
                width={1000}
                height={305}
                alt="Grid’s content hierarchy"
                className="mb-16 img-responsive"
              />
              <div className="page-content-item">
                <p className="font-sm mb-40">
                  Although the leadspace heights are changing, the typographic alignment from the
                  World overview page into the product keeps the user oriented.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-containment/screen-do-use-grids-content-hierarchy@2x.webp"
                    width={488}
                    height={483}
                    alt="Do use grids content hierarchy"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Do align type (outside of a container), components, and tiles to the columns.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    src="/images/general-components-containment/screen-do-not-grids-content-hierarchy@2x.webp"
                    width={488}
                    height={483}
                    alt="Do not use grids content hierarchy"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Do not take type off the column structure to achieve alignment.</p>
                  </div>
                </div>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'modal-dialog',
                    '12-column-grid',
                    'specifications',
                    'the-modals-anatomy',
                    'guidelines',
                    'modals-usage',
                    'grids-content-hierarchy',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/general-components/selection"
        previous="Selection"
        nextLink="/general-components/table"
        next="Table"
      />
    </div>
  </div>
);

const Containment = styled(ContainmentStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 5, 7, 8])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default Containment;
